/**
 * Кабинеты закупки, источники и партии.
 *
 * Цена контакта — снимок в партии на момент приёма: правка цены источника
 * не переписывает историю и не двигает вчерашний CPL (§1, §5.3).
 * Остановка источника не обрывает прозвон уже купленных номеров — они
 * остаются в очереди, перестают приниматься только новые.
 */
import { z } from 'zod';
import { bigIdSchema, businessDateSchema, isoDateTimeSchema, refIdSchema } from './common.dto.js';
/** Деньги наружу — строкой: numeric(10,4) в double не влезает без потерь */
export const moneySchema = z.string().regex(/^-?\d+(\.\d{1,4})?$/, 'ожидается число строкой, до 4 знаков после точки');
export const cabinetSchema = z.object({
    id: refIdSchema,
    code: z.string(),
    title: z.string(),
    provider: z.string(),
    contactCost: moneySchema,
    isActive: z.boolean(),
    /** Адрес приёма вебхука: секрет подписи наружу не отдаётся никогда */
    intakeUrl: z.string(),
});
export const createCabinetBodySchema = z.object({
    code: z.string().min(2).max(40).regex(/^[a-z0-9_-]+$/),
    title: z.string().min(2).max(200),
    provider: z.string().min(2).max(80),
    contactCost: moneySchema,
});
export const updateCabinetBodySchema = z.object({
    title: z.string().min(2).max(200).optional(),
    contactCost: moneySchema.optional(),
    isActive: z.boolean().optional(),
});
/** GET /api/sources/cabinets — секрета подписи в списке нет и не будет */
export const cabinetsResponseSchema = z.object({ items: z.array(cabinetSchema) });
/**
 * POST /api/sources/cabinets — единственный ответ, который несёт секрет подписи.
 * Показывается один раз: восстановить его нельзя, потерянный перевыпускается.
 */
export const createdCabinetResponseSchema = z.object({
    cabinet: cabinetSchema,
    webhookSecret: z.string(),
    /** Запасной адрес для поставщиков, которые умеют только URL и не умеют заголовки */
    intakeUrlWithSecret: z.string(),
});
/** POST /api/sources/cabinets/:id/secret — старая подпись перестаёт приниматься сразу */
export const rotatedCabinetSecretResponseSchema = createdCabinetResponseSchema.extend({
    intakeUrl: z.string(),
});
export const sourceSchema = z.object({
    id: refIdSchema,
    cabinetId: refIdSchema,
    cabinetTitle: z.string(),
    /** tag/project из вебхука: по нему номер привязывается к источнику */
    externalKey: z.string(),
    title: z.string().nullable(),
    contactCostOverride: moneySchema.nullable(),
    isActive: z.boolean(),
    stoppedAt: isoDateTimeSchema.nullable(),
    stopReason: z.string().nullable(),
    createdAt: isoDateTimeSchema,
    /** Тестовый источник (18.10.2026): номера не уходят роботу, заявки — только в телеграм */
    isTest: z.boolean(),
});
/** GET /api/sources */
export const sourcesResponseSchema = z.object({ items: z.array(sourceSchema) });
export const sourcesQuerySchema = z.object({
    cabinetId: z.coerce.number().int().positive().optional(),
    isActive: z.enum(['true', 'false']).optional(),
    search: z.string().max(120).optional(),
});
export const updateSourceBodySchema = z.object({
    title: z.string().min(1).max(200).nullable().optional(),
    contactCostOverride: moneySchema.nullable().optional(),
});
/** POST /api/sources/:id/stop — перестать принимать новые номера источника */
export const stopSourceBodySchema = z.object({
    reason: z.string().min(3).max(300),
});
/**
 * PATCH /api/sources/:id — название, цена, пауза и стоп одним запросом.
 *
 * Пауза (`isActive`) и стоп (`stopReason`) — разные вещи: паузу снимают
 * тумблером, стоп несёт причину, по которой источник выключили, и её потом
 * читают в разборе «почему просела закупка». `stopReason: null` снимает стоп.
 */
export const patchSourceBodySchema = updateSourceBodySchema.extend({
    isActive: z.boolean().optional(),
    stopReason: stopSourceBodySchema.shape.reason.nullable().optional(),
    isTest: z.boolean().optional(),
});
export const batchRowSchema = z.object({
    id: bigIdSchema,
    batchUid: z.string(),
    sourceId: refIdSchema,
    sourceTitle: z.string(),
    /** Цена контакта на момент приёма — снимок, не текущая настройка */
    contactCost: moneySchema,
    contactsCount: z.number().int().min(0),
    spend: moneySchema,
    businessDate: businessDateSchema,
    receivedAt: isoDateTimeSchema,
});
/**
 * GET /api/sources/:id — карточка источника: сам источник и его последние партии.
 *
 * Партии здесь не украшение: в каждой лежит снимок цены на момент приёма, и
 * когда расход за день не сходится с ожиданием, ответ виден именно тут — цену
 * поменяли между партиями (§1.5).
 */
export const sourceCardResponseSchema = z.object({
    source: sourceSchema,
    batches: z.array(batchRowSchema),
});
export const batchesQuerySchema = z.object({
    sourceId: z.coerce.number().int().positive().optional(),
    from: businessDateSchema.optional(),
    to: businessDateSchema.optional(),
    limit: z.coerce.number().int().min(1).max(500).default(100),
    offset: z.coerce.number().int().min(0).default(0),
});
//# sourceMappingURL=sources.dto.js.map